import React, { useEffect, useState } from 'react';
import { Button, Label, Radio, TextInput, Textarea, ToggleSwitch } from 'flowbite-react';
import Select, { MultiValue, SingleValue } from 'react-select';
import {
	BaseAPIResponse,
	InterviewFormatType,
	PositionEducationRequirementType,
	PositionTitle,
	PositionType,
	RegisterPosition,
	SingleRoundLabel,
	TechStacksForPosition,
	UIPosition,
	WorkingMode,
} from 'types';
import {
	Dropdown,
	camelCaseToSpace,
	designationMappingWithTechStack,
	generatePositionTitle,
	generateRangeValuesArray,
	generateTechStackName,
	isDecimal,
	locationPreferenceOptions,
	techStackMappingWithSkills,
} from 'shared-utils';
import { UseMutationResult } from 'react-query';
import { Loader } from 'ui';
import { useUserContext } from 'src/context/UserProvider';
import ReactQuill from 'react-quill';
import { IoMdAdd, IoMdRemove } from 'react-icons/io';
import moment from 'moment';
import _ from 'lodash';
import { PositionEducationRequirementTypeOptions } from 'src/utils/recruiter.util';
import SelectRoundType from './SelectRoundType';
import { useGetLMSTechStacksQuery, useGetDropdownItemsByCategory } from 'src/hooks/dropdownMenu.hooks';
import { LMSTechStack } from 'src/apis/dropdownMenu';
import {
	useGetPositionTitlesQuery,
	useGetTechStacksForPositionTitleQuery,
	useGetSkillsForTechStackQuery,
} from '../../hooks/positionConfiguration.hooks';
import type {
	PositionTitle as PositionTitleConfig,
	PositionTechStack as PositionTechStackConfig,
	Skill as PositionSkill,
} from 'src/apis/positionConfiguration';
interface Props {
	data: UIPosition;
	jobId: string;
	editPositionFn: UseMutationResult<BaseAPIResponse, unknown, { id: string; payload: RegisterPosition }, unknown>;
}

const EditPosition = ({ data, jobId, editPositionFn }: Props) => {
	const { isAccountManagerLoggedIn } = useUserContext();
	const [positionTypes, setPositionTypes] = useState<Dropdown[]>([]);
	const [positionTitles, setPositionTitles] = useState<Dropdown[]>([]);
	const [techStacks, setTechStacks] = useState<Dropdown[]>([]);
	const [skillsList, setSkillsList] = useState<Dropdown[]>([]);
	const [mandatoryselectedSkills, setMandatorySelectedSkills] = useState<Dropdown[]>([]);
	const [nonMandatoryselectedSkills, setNonMandatorySelectedSkills] = useState<Dropdown[]>([]);
	const [experienceInMonths, setExperienceInMonths] = useState({ min: 0, max: 0 });
	const [experienceInYears, setExperienceInYears] = useState({ min: 0, max: 0 });
	const [selectedExpInput, setSelectedExpInput] = useState<SingleValue<Dropdown>>({ value: 'years', label: 'Years' });
	const [expError, setExpError] = useState(false);
	const [, setSalaryValue] = useState<number[]>([7, 27]);
	const [, setExperienceValue] = useState<number[]>([0, 3]);
	const [autoRecommendationEnable, setAutoRecommendationEnable] = useState<boolean>(false);
	const [lmsTechStacks, setLmsTechStacks] = useState<Array<{ techStack: string; priority: number; _populated?: { name: string; label: string } }>>([]);
	const [subRoles, setSubRoles] = useState<string[]>([]);
	const [existingLmsTechStacks, setExistingLmsTechStacks] = useState<Array<{ techStack: string; priority: number; _populated?: { name: string; label: string } }>>([]);
	const [existingSubRoles, setExistingSubRoles] = useState<string[]>([]);
	const [prepMaterialLinks, setPrepMaterialLinks] = useState<string[]>(['']);
	const [showPrepMaterialToStudent, setShowPrepMaterialToStudent] = useState<boolean>(false);
	const { data: lmsTechStacksData, isLoading: isLoadingLMSTechStacks } = useGetLMSTechStacksQuery(undefined, true);
	const { data: subRoleData } = useGetDropdownItemsByCategory('SUB_ROLE', true, true); // Get SubRole items where showInMenu is true

	// Fetch position titles from database
	const { data: positionTitlesData, isLoading: isLoadingPositionTitles } = useGetPositionTitlesQuery();

	const [positionDetail, setPositionDetail] = useState<RegisterPosition>({
		title: '' as PositionTitle,
		techStack: '' as TechStacksForPosition,
		requiredBTech: false,
		experience: {
			min: 0,
			max: 2,
		},
		goodToHaveSkillNames: [],
		requiredSkillNames: [],
		location: '',
		workLocations: [],
		positionType: PositionType.OFFICE,
		salaryRange: {
			min: 10,
			max: 20,
		},
		description: '',
		vacancy: 0,
		numberOfRounds: 3,
		workingMode: WorkingMode.FULL_TIME,
		internshipSalary: {
			min: 5,
			max: 10,
		},
		internshipDuration: 0,
		hasBond: false,
		roundLabels: [] as SingleRoundLabel[],
	});
	const currentYear = moment().year();

	// Find position title ID from name (enum value) for fetching tech stacks
	// We store the name (enum value) in positionDetail.title, but need the _id to fetch tech stacks
	const selectedPositionTitleId = React.useMemo(() => {
		if (!positionDetail.title || !positionTitlesData?.positionTitles) return null;
		const foundTitle = positionTitlesData.positionTitles.find(
			(pt: PositionTitleConfig) => pt.name === positionDetail.title
		);
		return foundTitle?._id || null;
	}, [positionTitlesData, positionDetail.title]);

	// Fetch tech stacks for selected position title
	const { data: techStacksForTitleData, isLoading: isLoadingTechStacksForTitle } = useGetTechStacksForPositionTitleQuery(
		selectedPositionTitleId || null,
		!!positionDetail.title && !!selectedPositionTitleId,
	);

	// Find tech stack ID from name (enum value) for fetching skills
	const selectedTechStackId = React.useMemo(() => {
		return techStacksForTitleData?.techStacks?.find(
			(ts: PositionTechStackConfig) => ts.name === positionDetail.techStack
		)?._id;
	}, [techStacksForTitleData, positionDetail.techStack]);

	// Fetch skills for selected tech stack
	const { data: skillsForTechStackData, isLoading: isLoadingSkillsForTechStack } = useGetSkillsForTechStackQuery(
		selectedTechStackId || null,
		!!positionDetail.techStack && !!selectedTechStackId,
	);

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		editPositionFn.mutate({
			id: jobId,
			payload: {
				...positionDetail,
				goodToHaveSkillNames: nonMandatoryselectedSkills.map(skill => skill.value),
				requiredSkillNames: mandatoryselectedSkills.map(skill => skill.value),
				autoRecommendationEnable: autoRecommendationEnable,
				lmsTechStack: autoRecommendationEnable && lmsTechStacks.length > 0
					? lmsTechStacks.map(ts => {
						// Ensure techStack is always a string (ObjectId)
						// Handle case where techStack might be an object
						let techStackId: string;
						const techStackValue = ts.techStack;
						if (typeof techStackValue === 'string') {
							techStackId = techStackValue;
						} else if (techStackValue && typeof techStackValue === 'object') {
							// If it's an object, try to get _id or toString()
							const techStackObj = techStackValue as any;
							if (techStackObj._id) {
								techStackId = String(techStackObj._id);
							} else if (typeof techStackObj.toString === 'function') {
								techStackId = techStackObj.toString();
							} else {
								techStackId = String(techStackValue);
							}
						} else {
							techStackId = String(techStackValue);
						}
						return {
							techStack: techStackId,
							priority: ts.priority || 0,
						};
					})
					: undefined,
				subRole: autoRecommendationEnable && subRoles.length > 0 ? subRoles : undefined,
				prepMaterial: prepMaterialLinks.filter(link => link.trim() !== ''),
				showPrepMaterialToStudent,
			},
		});
	};

	useEffect(() => {
		const types = Object.values(PositionType).map(type => ({ label: type, value: type }));
		setPositionTypes(types);

		// Use database position titles if available, otherwise fallback to enum
		if (positionTitlesData?.positionTitles) {
			const titles = positionTitlesData.positionTitles.map((title: PositionTitleConfig) => ({
				label: title.label || title.name,
				value: title.name, // Use name (enum value) to store as string, not _id
			}));
			setPositionTitles(titles);
		} else {
			// No fallback - only use database values
			setPositionTitles([]);
		}
	}, [positionTitlesData]);

	// Tech stacks will be set by the useEffect that depends on positionDetail.title and techStacksForTitleData
	// This is handled in the useEffect above

	useEffect(() => {
		if (data.title && positionTypes.length && positionTitles.length) {
			const newP = {
				title: data.title as PositionTitle,
				techStack: data.techStack as TechStacksForPosition,
				requiredBTech: data.requiredBTech ?? false,
				minGradYear: data.positionRequirement.minGradYear,
				experience: data.positionRequirement.workExperience,
				goodToHaveSkillNames: [],
				requiredSkillNames: [],
				location: data.workLocation,
				workLocations: data.workLocations || [],
				positionType: data.positionType as PositionType,
				salaryRange: data.salary,
				description: data.description,
				numberOfRounds: data.numberOfRounds ?? 3,
				workingMode: data.workingMode,
				vacancy: data.vacancy,
				hasBond: data.hasBond,
				bondDetails: data?.bondDetails,
				internshipSalary: {
					min: 5,
					max: 10,
				},
				internshipDuration: 0,
				onlyForMasaiOneStudents: data.onlyForMasaiOneStudents,
				isFaceToFacePosition: data?.isFaceToFacePosition,
				interviewFormat: data?.interviewFormat,
				hasAssignmentRound: data?.hasAssignmentRound,
				educationRequirements: data?.positionRequirement?.educationRequirements,
			};

			if (
				isDecimal(data.positionRequirement.workExperience?.min) ||
				isDecimal(data.positionRequirement.workExperience?.max)
			) {
				setSelectedExpInput({ value: 'months', label: 'Months' });
			}

			setExperienceInYears({
				min: 0,
				max: 0,
			});
			setExperienceInMonths({
				min: Math.ceil(data.positionRequirement.workExperience?.min * 12),
				max: Math.ceil(data?.positionRequirement.workExperience?.max * 12),
			});

			if (data.workingMode === WorkingMode.INTERNSHIP) {
				setPositionDetail({
					...newP,
					internshipDuration: data?.internshipDuration || 0,
					internshipSalary: {
						min: data?.internshipSalary?.min || 5,
						max: data?.internshipSalary?.max || 10,
					},
				});
			} else if (data.workingMode === WorkingMode.INTERNSHIP_TO_FULL_TIME) {
				setPositionDetail({
					...newP,
					internshipDuration: data?.internshipDuration || 0,
					internshipSalary: {
						min: data?.internshipSalary?.min || 5,
						max: data?.internshipSalary?.max || 10,
					},
				});
			} else {
				setPositionDetail(newP);
			}

			// set round labels
			setPositionDetail(prev => ({
				...prev,
				roundLabels: data?.roundLabels || [],
			}));

			const goodToHaveSkills =
				data.positionRequirement?.goodToHaveskills?.map(skill => ({ label: skill.name, value: skill.name })) || [];
			const technicalSkills =
				data.positionRequirement?.skillThresholds?.map(skillthreshold => ({
					label: skillthreshold.displaySkill,
					value: skillthreshold.displaySkill,
				})) || [];
			setNonMandatorySelectedSkills(goodToHaveSkills);
			setMandatorySelectedSkills(technicalSkills);

			setExperienceValue([data.positionRequirement.workExperience.min, data.positionRequirement.workExperience.max]);
			setSalaryValue([data.salary.min, data.salary.max]);

			// Initialize auto-recommendation fields
			if (data.autoRecommendationEnable !== undefined) {
				setAutoRecommendationEnable(data.autoRecommendationEnable);
			}

			// Initialize existing lmsTechStack (for display - cannot be removed)
			if (data.lmsTechStack && Array.isArray(data.lmsTechStack) && data.lmsTechStack.length > 0) {
				const existing = data.lmsTechStack.map((ts: any) => {
					// Handle populated data (techStack is an object with _id, name, label) or unpopulated (just ObjectId)
					// Structure when populated: { techStack: { _id: '...', name: '...', label: '...' }, priority: 0 }
					// Structure when unpopulated: { techStack: ObjectId('...'), priority: 0 }
					let techStackId: string;
					let populatedData: { name: string; label: string } | undefined;

					if (typeof ts === 'string') {
						techStackId = ts;
					} else if (ts && typeof ts === 'object') {
						if (ts.techStack) {
							if (typeof ts.techStack === 'object' && ts.techStack !== null && ts.techStack._id) {
								// Populated: techStack is an object with _id, name, label
								techStackId = String(ts.techStack._id);
								populatedData = {
									name: ts.techStack.name || String(ts.techStack._id),
									label: ts.techStack.label || ts.techStack.name || String(ts.techStack._id),
								};
							} else {
								// Unpopulated: techStack is just an ObjectId or string
								techStackId = String(ts.techStack);
							}
						} else {
							// ts itself might be the ObjectId
							techStackId = String(ts);
						}
					} else {
						techStackId = String(ts);
					}

					return {
						techStack: String(techStackId), // Ensure it's always a string
						priority: (ts && typeof ts === 'object' && ts.priority !== undefined) ? ts.priority : 0,
						_populated: populatedData,
					};
				});
				setExistingLmsTechStacks(existing);
				setLmsTechStacks(existing); // Start with existing values
			}

			// Initialize existing subRole (for display - cannot be removed)
			if (data.subRole && Array.isArray(data.subRole) && data.subRole.length > 0) {
				setExistingSubRoles(data.subRole);
				setSubRoles(data.subRole); // Start with existing values
			}

			// Initialize prepMaterial
			if (data.prepMaterial && Array.isArray(data.prepMaterial) && data.prepMaterial.length > 0) {
				setPrepMaterialLinks(data.prepMaterial);
			} else {
				setPrepMaterialLinks(['']);
			}

			// Initialize showPrepMaterialToStudent
			if (data.showPrepMaterialToStudent !== undefined) {
				setShowPrepMaterialToStudent(data.showPrepMaterialToStudent);
			}
		}
	}, [data, positionTitles, positionTypes]);

	// Update lmsTechStacks when data loads to ensure Select component displays existing values
	useEffect(() => {
		if (lmsTechStacksData?.lmsTechStacks && existingLmsTechStacks.length > 0) {
			// Ensure existing values are properly set when dropdown data is available
			// This ensures the Select component can display the selected values
			setLmsTechStacks(prev => {
				// Preserve existing items and merge with any new selections
				// Ensure all techStack values are strings
				const existingMap = new Map(existingLmsTechStacks.map(ts => {
					const techStackId = typeof ts.techStack === 'string' ? ts.techStack : String(ts.techStack);
					return [techStackId, { ...ts, techStack: techStackId }];
				}));
				const newMap = new Map(prev
					.filter(ts => !existingLmsTechStacks.some(ets => {
						const etsId = typeof ets.techStack === 'string' ? ets.techStack : String(ets.techStack);
						const tsId = typeof ts.techStack === 'string' ? ts.techStack : String(ts.techStack);
						return etsId === tsId;
					}))
					.map(ts => {
						const techStackId = typeof ts.techStack === 'string' ? ts.techStack : String(ts.techStack);
						return [techStackId, { ...ts, techStack: techStackId }];
					}));
				const merged = Array.from(new Map([...existingMap, ...newMap]).values());
				return merged;
			});
		}
	}, [lmsTechStacksData?.lmsTechStacks, existingLmsTechStacks]);

	// Use database tech stacks if available, otherwise fallback to enum mapping
	useEffect(() => {
		if (techStacksForTitleData?.techStacks && positionDetail.title) {
			const stacks = techStacksForTitleData.techStacks.map((ts: PositionTechStackConfig) => ({
				label: ts.label || ts.name,
				value: ts.name, // Use name (enum value) for backward compatibility with backend
			}));
			setTechStacks(stacks);
		} else {
			// No fallback - only use database values
			setTechStacks([]);
		}
	}, [positionDetail.title, techStacksForTitleData]);

	// Separate skills into required (all skills) and good to have (only soft skills)
	const requiredSkillsList = React.useMemo<Dropdown[]>(() => {
		// Use database skills if available, otherwise fallback to enum mapping
		if (skillsForTechStackData?.skills && positionDetail.techStack) {
			const skills = skillsForTechStackData.skills.map((skill: PositionSkill) => ({
				label: skill.name,
				value: skill.name, // Use skill name (matching existing skillNameToIdMapping)
			}));
			return skills;
		} else if (positionDetail.techStack && typeof positionDetail.techStack === 'string' && Object.values(TechStacksForPosition).includes(positionDetail.techStack as TechStacksForPosition)) {
			// Fallback to enum mapping for backward compatibility
			return techStackMappingWithSkills[positionDetail.techStack as TechStacksForPosition]?.map(s => ({ label: s, value: s })) || [];
		}
		return [];
	}, [positionDetail.techStack, skillsForTechStackData]);

	// Good to have skills: only soft skills (isSoftSkill === true)
	const goodToHaveSkillsList = React.useMemo<Dropdown[]>(() => {
		// Use database skills if available, filter by isSoftSkill
		if (skillsForTechStackData?.skills && positionDetail.techStack) {
			const softSkills = skillsForTechStackData.skills
				.filter((skill: PositionSkill) => skill.isSoftSkill === true)
				.map((skill: PositionSkill) => ({
					label: skill.name,
					value: skill.name,
				}));
			return softSkills;
		}
		// Fallback: if using enum-based, we don't have isSoftSkill info, so return empty
		// Admin should configure skills in database for proper filtering
		return [];
	}, [positionDetail.techStack, skillsForTechStackData]);

	useEffect(() => {
		// Set all skills list for backward compatibility (used in some places)
		setSkillsList(requiredSkillsList);
	}, [requiredSkillsList]);

	const labelStyle = `dark:text-white text-[#111928]`;

	const expRange = {
		minYear: Math.floor(positionDetail?.experience?.min),
		minMonth: Math.ceil(positionDetail?.experience?.min * 12) % 12,
		maxYear: Math.floor(positionDetail?.experience?.max),
		maxMonth: Math.ceil(positionDetail?.experience?.max * 12) % 12,
	};

	return (
		<form className="flex font-inter flex-col gap-4 mt-[20px]" onSubmit={handleSubmit}>
			{/* Job Title */}
			<div>
				<div className="mb-2 block">
					<Label htmlFor="title" value="Job title" className={`${labelStyle}`} />
					<span className="text-red-600 pl-1">*</span>
				</div>
				<Select
					menuPlacement={'auto'}
					classNamePrefix="my-select-plans"
					className="single-value-select-tag"
					options={positionTitles}
					value={positionTitles.find(title => title.value === positionDetail.title)}
					onChange={(val: SingleValue<Dropdown>) => {
						if (val?.value) {
							setMandatorySelectedSkills([]);
							setNonMandatorySelectedSkills([]);
							setPositionDetail({
								...positionDetail,
								techStack: '' as TechStacksForPosition,
								requiredSkillNames: [],
								goodToHaveSkillNames: [],
								title: val.value as PositionTitle,
							});
						}
					}}
					placeholder="SDE 3"
				/>
			</div>

			{/* Tech stack for position */}
			<div>
				<div className="mb-2 block">
					<Label htmlFor="techStack" value="Tech Stack" className={`${labelStyle}`} />
					<span className="text-red-600 pl-1">*</span>
				</div>
				<Select
					options={techStacks}
					value={techStacks?.find(techStack => techStack.value === positionDetail.techStack) ?? null}
					menuPlacement={'auto'}
					classNamePrefix="my-select-plans"
					className="single-value-select-tag"
					onChange={(val: SingleValue<Dropdown>) => {
						if (val?.value) {
							setMandatorySelectedSkills([]);
							setNonMandatorySelectedSkills([]);
							setPositionDetail({
								...positionDetail,
								requiredSkillNames: [],
								goodToHaveSkillNames: [],
								techStack: val.value as TechStacksForPosition,
							});
						}
					}}
					placeholder="MERN"
				/>
			</div>

			{/* Auto Recommendation Toggle */}
			<div>
				<div className="mb-2 block">
					<Label htmlFor="autoRecommendationEnable" value="Auto Recommendation Enabled" className={`${labelStyle}`} />
					<span className="text-sm text-gray-500 ml-2">(Cannot be changed after creation)</span>
				</div>
				<ToggleSwitch
					checked={autoRecommendationEnable}
					disabled={true}
					onChange={e => {
						setAutoRecommendationEnable(e);
						if (!e) {
							// When disabled, keep existing values but don't allow new additions
							setLmsTechStacks(existingLmsTechStacks);
							setSubRoles(existingSubRoles);
						}
					}}
				/>
			</div>

			{/* LMS TechStack Multi-Select - Add only, cannot remove existing */}
			{autoRecommendationEnable && (
				<div>
					<div className="mb-2 block">
						<Label htmlFor="lmsTechStack" value="LMS TechStack" className={`${labelStyle}`} />
						<span className="text-red-600 pl-1">*</span>
						{existingLmsTechStacks.length > 0 && (
							<span className="text-sm text-gray-500 ml-2">(Existing: {existingLmsTechStacks.length} - cannot be removed)</span>
						)}
					</div>
					<Select
						menuPlacement={'auto'}
						classNamePrefix="my-select-plans"
						isMulti
						closeMenuOnSelect={false}
						isLoading={isLoadingLMSTechStacks}
						options={lmsTechStacksData?.lmsTechStacks
							?.filter((techStack: LMSTechStack) => techStack._id && techStack.isActive !== false)
							.map((techStack: LMSTechStack) => ({
								label: techStack.label || techStack.name,
								value: String(techStack._id!),
							})) || []}
						value={(() => {
							// Return empty array if dropdown data or selected items are not available
							if (!lmsTechStacksData?.lmsTechStacks || !Array.isArray(lmsTechStacksData.lmsTechStacks) || lmsTechStacks.length === 0) {
								return [];
							}

							// Find matching tech stacks and create value array for Select component
							const selectedOptions = lmsTechStacksData.lmsTechStacks
								.filter((techStack: LMSTechStack) => {
									if (!techStack || !techStack._id) return false;
									const techStackId = String(techStack._id);
									return lmsTechStacks.some(ts => {
										const tsId = String(ts.techStack);
										return tsId === techStackId;
									});
								})
								.map((techStack: LMSTechStack) => ({
									label: techStack.label || techStack.name || String(techStack._id),
									value: String(techStack._id!),
								}));

							return selectedOptions;
						})()}
						isOptionDisabled={(option) => {
							// Disable existing options so they cannot be removed
							return existingLmsTechStacks.some(ets => String(ets.techStack) === String(option.value));
						}}
						onChange={(vals: MultiValue<{ label: string; value: string }>) => {
							const selectedValues = vals || [];
							const existingTechStackIds = new Set(existingLmsTechStacks.map(ts => String(ts.techStack)));

							// Get new selections that aren't in existing
							// Priority is auto-assigned based on index (0, 1, 2, etc.) - no user input needed
							const newSelections = selectedValues
								.filter(val => !existingTechStackIds.has(String(val.value)))
								.map((val, index) => {
									// Use index as priority (existing items keep their priority, new ones get sequential)
									const nextPriority = lmsTechStacks.length;
									return {
										techStack: String(val.value),
										priority: nextPriority + index,
									};
								});

							// Always include existing + new selections (merge to avoid duplicates)
							const existingMap = new Map(existingLmsTechStacks.map(ts => [String(ts.techStack), ts]));
							const newMap = new Map(newSelections.map(ts => [String(ts.techStack), ts]));
							const merged = Array.from(new Map([...existingMap, ...newMap]).values());
							setLmsTechStacks(merged);
						}}
						placeholder="Select additional LMS TechStack(s)"
					/>
					{existingLmsTechStacks.length > 0 && (
						<div className="mt-2 text-sm text-gray-600">
							Existing LMS TechStacks: {existingLmsTechStacks
								.map((ts) => {
									try {
										// First try to use populated data from backend
										const populated = (ts as any)._populated;
										if (populated && typeof populated === 'object' && populated !== null && !Array.isArray(populated)) {
											if (populated.label && typeof populated.label === 'string') {
												return populated.label;
											}
											if (populated.name && typeof populated.name === 'string') {
												return populated.name;
											}
										}
										// Fallback to lookup from dropdown data
										if (lmsTechStacksData?.lmsTechStacks) {
											const techStack = lmsTechStacksData.lmsTechStacks.find((t: LMSTechStack) => {
												if (!t._id) return false;
												return String(t._id) === String(ts.techStack);
											});
											if (techStack) {
												return techStack.label || techStack.name || String(ts.techStack);
											}
										}
										// Last resort: return the ID as string
										return String(ts.techStack);
									} catch (error) {
										// If anything fails, just return the ID
										return String(ts.techStack);
									}
								})
								.filter((name): name is string => typeof name === 'string' && name.length > 0)
								.join(', ')}
						</div>
					)}
					{lmsTechStacks.length > existingLmsTechStacks.length && (
						<div className="mt-2 text-sm text-gray-600">
							Additional Selected: {lmsTechStacks
								.filter(ts => !existingLmsTechStacks.some(ets => String(ets.techStack) === String(ts.techStack)))
								.map((ts) => {
									const techStack = lmsTechStacksData?.lmsTechStacks?.find((t: LMSTechStack) => String(t._id) === String(ts.techStack));
									return techStack ? (techStack.label || techStack.name) : String(ts.techStack);
								}).join(', ')}
						</div>
					)}
				</div>
			)}

			{/* SubRole Multi-Select - Add only, cannot remove existing */}
			{autoRecommendationEnable && (
				<div>
					<div className="mb-2 block">
						<Label htmlFor="subRole" value="SubRole" className={`${labelStyle}`} />
						<span className="text-red-600 pl-1">*</span>
						{existingSubRoles.length > 0 && (
							<span className="text-sm text-gray-500 ml-2">(Existing: {existingSubRoles.length} - cannot be removed)</span>
						)}
					</div>
					<Select
						menuPlacement={'auto'}
						classNamePrefix="my-select-plans"
						isMulti
						closeMenuOnSelect={false}
						options={subRoleData?.lmsTechStacks?.map(item => ({
							label: item.label || item.name, // Use label for display, fallback to name
							value: item.name, // Store name (enum value) in database
						})) || []}
						value={subRoleData?.lmsTechStacks
							?.filter(item => subRoles.includes(item.name))
							.map(item => ({
								label: item.label || item.name, // Display label
								value: item.name // Value is name (enum value)
							})) || []}
						isOptionDisabled={(option) => {
							// Disable existing options so they cannot be removed
							return existingSubRoles.includes(option.value);
						}}
						onChange={(vals: MultiValue<{ label: string; value: string }>) => {
							const selectedValues = vals || [];
							const existingSubRoleSet = new Set(existingSubRoles);

							// Get new selections that aren't in existing
							const newSelections = selectedValues
								.filter(val => !existingSubRoleSet.has(val.value))
								.map(val => val.value);

							// Always include existing + new selections (merge to avoid duplicates)
							const merged = Array.from(new Set([...existingSubRoles, ...newSelections]));
							setSubRoles(merged);
						}}
						placeholder="Select additional SubRole(s)"
					/>
					{existingSubRoles.length > 0 && (
						<div className="mt-2 text-sm text-gray-600">
							Existing subRoles: {existingSubRoles.join(', ')}
						</div>
					)}
				</div>
			)}

			{/* Job type */}
			<div>
				<div className="mb-2 block">
					<Label htmlFor="title" value="Job type" className={`${labelStyle}`} />
					<span className="text-red-600 pl-1">*</span>
				</div>

				<fieldset
					className="flex flex-wrap max-w-md gap-4"
					onChange={e => {
						const target = e.target as HTMLInputElement;
						if (target?.value) {
							setPositionDetail({ ...positionDetail, workingMode: target?.value as WorkingMode });
						}
					}}
				>
					{WorkingMode &&
						Object.values(WorkingMode).map((mode: string, i: number) => (
							<div key={i} className="flex items-center gap-2">
								<Radio
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={mode}
									name="workingMode"
									value={mode}
									checked={positionDetail?.workingMode === mode}
								/>
								<Label htmlFor={mode}>{camelCaseToSpace(mode)}</Label>
							</div>
						))}
				</fieldset>
			</div>

			{/* Number of rounds */}
			<div>
				<div className="mb-2 block">
					<Label htmlFor="title" value="Number of rounds" className={`${labelStyle}`} />
					<span className="text-red-600 pl-1">*</span>
				</div>

				<div className="flex items-center gap-[8px]">
					<Button
						className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
						color="light"
						disabled={positionDetail.numberOfRounds <= 1}
						onClick={() => {
							setPositionDetail({ ...positionDetail, numberOfRounds: --positionDetail.numberOfRounds });
						}}
					>
						<IoMdRemove className="w-[24px] h-[24px]" />
					</Button>

					<TextInput
						min={1}
						max={10}
						onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
						type="number"
						placeholder="2"
						name="numberOfRounds"
						value={positionDetail.numberOfRounds}
						onChange={event => {
							if (+event?.target?.value <= 10)
								setPositionDetail({ ...positionDetail, numberOfRounds: +event?.target?.value });
						}}
						className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow"
					/>

					<Button
						className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
						color="light"
						disabled={positionDetail.numberOfRounds >= 10}
						onClick={() => {
							setPositionDetail({ ...positionDetail, numberOfRounds: ++positionDetail.numberOfRounds });
						}}
					>
						<IoMdAdd className="w-[24px] h-[24px]" />
					</Button>
				</div>
			</div>

			<SelectRoundType
				onChange={(roundLabels: SingleRoundLabel[]) => {
					roundLabels = roundLabels.filter(roundLabel => roundLabel.type);
					console.log('final round labels:', roundLabels);
					setPositionDetail(prev => ({ ...prev, roundLabels }));
				}}
				value={positionDetail.roundLabels || []}
				numberOfRounds={positionDetail?.numberOfRounds}
			/>

			{/* Minimum Grad Year */}
			{isAccountManagerLoggedIn ? (
				<div>
					<div className="mb-2 block">
						<Label htmlFor="minGradYear" value="Minimum Grad Year" className={`${labelStyle}`} />
					</div>

					<div className="flex items-center gap-[8px]">
						<Button
							className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
							color="light"
							disabled={(positionDetail.minGradYear || currentYear) <= 1950}
							onClick={() => {
								setPositionDetail({ ...positionDetail, minGradYear: (positionDetail.minGradYear || currentYear) - 1 });
							}}
						>
							<IoMdRemove className="w-[24px] h-[24px]" />
						</Button>

						<TextInput
							min={1950}
							max={currentYear}
							onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
							type="number"
							placeholder={currentYear.toString()}
							name="minGradYear"
							value={positionDetail.minGradYear}
							onChange={event => {
								if (+event?.target?.value <= currentYear)
									setPositionDetail({ ...positionDetail, minGradYear: +event?.target?.value });
							}}
							className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow"
						/>

						<Button
							className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
							color="light"
							disabled={(positionDetail.minGradYear || currentYear) >= currentYear}
							onClick={() => {
								setPositionDetail({ ...positionDetail, minGradYear: (positionDetail.minGradYear || currentYear) + 1 });
							}}
						>
							<IoMdAdd className="w-[24px] h-[24px]" />
						</Button>
					</div>
				</div>
			) : null}

			{/* Education Requirement */}
			<div>
				<div className="mb-2 block">
					<Label htmlFor="title" value="Educational qualifications required (optional)" className={`${labelStyle}`} />
				</div>
				<Select
					menuPlacement={'auto'}
					styles={{
						multiValue: (baseStyles, state) => ({
							...baseStyles,
							backgroundColor: '#111E2C',
						}),
					}}
					closeMenuOnSelect={false}
					blurInputOnSelect={false}
					classNamePrefix="my-select-plans"
					className="single-value-select-tag"
					options={PositionEducationRequirementTypeOptions}
					value={positionDetail?.educationRequirements?.map(degree => ({
						label: degree,
						value: degree,
					}))}
					isMulti={true}
					onChange={(value: MultiValue<Dropdown>) => {
						const selectedDegrees = value.map(item => item.value);
						setPositionDetail({
							...positionDetail,
							educationRequirements: selectedDegrees as PositionEducationRequirementType[],
						});
					}}
					placeholder="B.Tech, BCA/MCA"
				/>
			</div>

			{/* Required B.Tech only */}
			{isAccountManagerLoggedIn ? (
				<div className="flex items-center flex-wrap gap-[20%]">
					{/* <div>
						<div className="mb-2 block">
							<Label htmlFor="title" value="Required B.Tech/BE only" className={`${labelStyle}`} />
						</div>

						<ToggleSwitch
							checked={positionDetail.requiredBTech || false}
							onChange={e => {
								console.log('289');
								setPositionDetail(prev => ({ ...prev, requiredBTech: e }));
							}}
						/>
					</div> */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="title" value="Only for Masai One Students?" className={`${labelStyle}`} />
						</div>

						<ToggleSwitch
							checked={positionDetail.onlyForMasaiOneStudents || false}
							onChange={e => {
								console.log('474');
								setPositionDetail(prev => ({ ...prev, onlyForMasaiOneStudents: e }));
							}}
						/>
					</div>
				</div>
			) : null}

			<div>
				<div className="mb-2 block">
					<Label htmlFor="title" value="Interview format" className={`${labelStyle}`} />
					<span className="text-red-600 pl-1">*</span>
				</div>

				<fieldset
					className="flex flex-wrap max-w-md gap-8"
					onChange={e => {
						const target = e.target as HTMLInputElement;
						console.log('target?.value:', target?.value);
						if (target?.value) {
							setPositionDetail(prev => ({
								...prev,
								isFaceToFacePosition: target.value === InterviewFormatType.INPERSON,
							}));
						}
					}}
				>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={InterviewFormatType.ONLINE}
							name="interviewFormat"
							value={InterviewFormatType.ONLINE}
							checked={positionDetail?.isFaceToFacePosition ? false : true}
						/>
						<Label htmlFor={InterviewFormatType.ONLINE}>Online</Label>
					</div>

					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={InterviewFormatType.INPERSON}
							name="interviewFormat"
							value={InterviewFormatType.INPERSON}
							checked={positionDetail?.isFaceToFacePosition ? true : false}
						/>
						<Label htmlFor={InterviewFormatType.INPERSON}>In-person</Label>
					</div>
				</fieldset>
			</div>
			<div>
				<div className="mb-2 block">
					<Label htmlFor="title" value="Includes Bond Agreement" className={`${labelStyle}`} />
				</div>
				{/* For Bond */}
				<fieldset
					className="flex flex-wrap max-w-md gap-8"
					onChange={e => {
						const target = e.target as HTMLInputElement;
						if (target?.value) {
							setPositionDetail(prev => ({ ...prev, hasBond: target.value === 'true', bondDetails: '' }));
						}
					}}
				>
					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'yes'}
							name="hasBond"
							value={'true'}
							checked={positionDetail?.hasBond === true}
						/>
						<Label htmlFor={'yes'}>Yes</Label>
					</div>

					<div className="flex items-center gap-2">
						<Radio
							className="text-[#7777E7] focus:ring-[#7777E7]"
							id={'no'}
							name="hasBond"
							value={'false'}
							checked={positionDetail?.hasBond === false}
						/>
						<Label htmlFor={'no'}>No</Label>
					</div>
				</fieldset>
			</div>

			{/* For Bond Details */}
			{positionDetail?.hasBond ? (
				<div>
					<div className="mb-2 flex justify-between items-end">
						<div>
							<Label className={`${labelStyle}`} htmlFor="message" value="Bond Agreement Details" />
						</div>

						<span className="italic text-xs text-[#6B7280] ">
							{(positionDetail?.bondDetails?.length || 0) + ' / ' + 1000}
						</span>
					</div>

					<Textarea
						style={{
							fontFamily: "'Inter', sans-serif",
							borderRadius: '8px',
							backgroundColor: '#f9fafb',
						}}
						id="message"
						name="message"
						maxLength={1000}
						value={positionDetail?.bondDetails}
						placeholder="Write your message here..."
						className="border bg-white dark:text-white dark:border-[#4B5563] min-h-[100px] dark:bg-[#374151] focus:border-transparent focus:!ring-[1px] focus:!ring-[#7777E7]"
						onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
							setPositionDetail({ ...positionDetail, bondDetails: e.target.value })
						}
					/>
				</div>
			) : null}

			{/* Must have skills */}
			<div>
				<div className="mb-2 block">
					<Label htmlFor="title" value="Must have skills (min 2 skills required)" className={`${labelStyle}`} />
					<span className="text-red-600 pl-1">*</span>
				</div>
				<Select
					menuPlacement={'auto'}
					classNamePrefix="my-select-plans"
					className="single-value-select-tag"
					closeMenuOnSelect={false}
					blurInputOnSelect={false}
					isLoading={isLoadingSkillsForTechStack}
					options={requiredSkillsList.filter((skill: Dropdown) => !nonMandatoryselectedSkills.map(ms => ms.value).includes(skill.value))}
					isMulti={true}
					styles={{
						multiValue: (baseStyles, state) => ({
							...baseStyles,
							backgroundColor: '#111E2C',
						}),
					}}
					value={mandatoryselectedSkills}
					onChange={(value: MultiValue<Dropdown>) => {
						setMandatorySelectedSkills(value as Dropdown[]);
					}}
					placeholder="Django  React  Python  Java"
				/>
			</div>

			{/* Good to have skills - Only soft skills */}
			<div>
				<div className="mb-2 block">
					<Label htmlFor="title" value="Good to have skills" className={`${labelStyle}`} />
				</div>
				<Select
					menuPlacement={'auto'}
					closeMenuOnSelect={false}
					blurInputOnSelect={false}
					classNamePrefix="my-select-plans"
					className="single-value-select-tag"
					isLoading={isLoadingSkillsForTechStack}
					options={goodToHaveSkillsList.filter((skill: Dropdown) => !mandatoryselectedSkills.map(ms => ms.value).includes(skill.value))}
					isMulti={true}
					styles={{
						multiValue: (baseStyles, state) => ({
							...baseStyles,
							backgroundColor: '#111E2C',
						}),
					}}
					value={nonMandatoryselectedSkills}
					placeholder="Communication Java"
					onChange={(value: MultiValue<Dropdown>) => {
						setNonMandatorySelectedSkills(value as Dropdown[]);
					}}
				/>
			</div>

			{/* Experience Required */}
			<div>
				<div className="flex w-full justify-between">
					<div className="mb-1 block">
						<Label htmlFor="title" value="Experience required (in years, months)" className={`${labelStyle}`} />
						<span className="text-red-600 pl-1">*</span>
					</div>

					<Select
						menuPlacement={'auto'}
						classNamePrefix="my-select-plans"
						className="single-value-select-tag"
						options={[
							{ value: 'months', label: 'Months' },
							{ value: 'years', label: 'Years' },
						]}
						value={selectedExpInput}
						onChange={(data: SingleValue<Dropdown>) => {
							setPositionDetail({
								...positionDetail,
								experience: { min: 0, max: 0 },
							});
							setExperienceInMonths({ min: 0, max: 0 });
							setSelectedExpInput(data);
						}}
						placeholder=""
					/>
				</div>

				{positionDetail?.experience ? (
					<div className="text-xs">
						Preview Exp. Range - ({expRange?.minYear} {expRange?.minYear > 1 ? 'Years ' : 'Year '}
						{expRange?.minMonth} {expRange?.minMonth > 1 ? 'Months ' : 'Month '}
						{'to '}
						{expRange?.maxYear} {expRange?.maxYear > 1 ? 'Years ' : 'Year '}
						{expRange?.maxMonth} {expRange?.maxMonth > 1 ? 'Months' : 'Month'})
					</div>
				) : null}

				<div className="flex items-center gap-2 mt-2">
					<div className="flex items-center gap-2">
						{selectedExpInput?.value === 'months' ? (
							<TextInput
								placeholder="Min Months"
								type="number"
								max={100}
								pattern="\d*"
								step={1}
								value={experienceInMonths.min}
								onChange={e => {
									const selectedValue = Number(e?.target?.value.replace(/\D/g, '') || 0);
									setExperienceInMonths({
										min: Number(selectedValue),
										max: selectedValue > experienceInMonths?.max ? selectedValue : experienceInMonths?.max,
									});

									const calcValue = Math.floor(+(selectedValue / 12 + experienceInYears?.min) * 100) / 100;

									setPositionDetail({
										...positionDetail,
										experience: {
											min: calcValue,
											max: selectedValue > experienceInMonths?.max ? calcValue : positionDetail?.experience?.max || 0,
										},
									});
								}}
							/>
						) : (
							<TextInput
								placeholder="Min Years"
								type="number"
								max={100}
								pattern="\d*"
								step={1}
								value={positionDetail.experience.min}
								onChange={e => {
									const selectedValue = +e.target.value.replace(/\D/g, '');
									setPositionDetail({
										...positionDetail,
										experience: {
											max:
												selectedValue > positionDetail.experience.max ? selectedValue : positionDetail.experience.max,
											min: selectedValue,
										},
									});
								}}
							/>
						)}
						{/*<Select
									menuPlacement={'auto'}
									classNamePrefix="my-select-plans"
									className="single-value-select-tag hideIndicator min-w-[50px]"
									options={generateRangeValuesArray(81, 0)}
									value={{
										label: String(experienceInYears?.min),
										value: String(experienceInYears?.min),
									}}
									onChange={(data: SingleValue<Dropdown>) => {
										const selectedValue = Number(data?.value || 0);

										setExperienceInYears({
											min: selectedValue,
											max: experienceInYears.max < selectedValue ? selectedValue : experienceInYears.max,
										});

										const calcValue = Math.floor(+(experienceInMonths?.min / 12 + selectedValue) * 100) / 100;
										const calcMaxValue =
											experienceInYears.max < selectedValue
												? Math.floor(+(experienceInMonths?.max / 12 + selectedValue) * 100) / 100
												: positionDetail.experience.max;

										setPositionDetail({
											...positionDetail,
											experience: {
												min: calcValue,
												max: calcMaxValue || 0,
											},
										});
									}}
									placeholder="Min Years"
								/>
								 Y
								<Select
									menuPlacement={'auto'}
									styles={{
										multiValue: (baseStyles, state) => ({
											...baseStyles,
											backgroundColor: '#111E2C',
										}),
									}}
									classNamePrefix="my-select-plans"
									className="single-value-select-tag hideIndicator min-w-[50px]"
									options={generateRangeValuesArray(12, 0)}
									value={{
										label: String(experienceInMonths?.min),
										value: String(experienceInMonths?.min),
									}}
									onChange={(data: SingleValue<Dropdown>) => {
										const selectedValue = Number(data?.value || 0);
										setExperienceInMonths({
											min: Number(selectedValue),
											max: experienceInMonths?.max,
										});

										const calcValue = Math.floor(+(selectedValue / 12 + experienceInYears?.min) * 100) / 100;

										setPositionDetail({
											...positionDetail,
											experience: {
												min: calcValue,
												max: positionDetail?.experience?.max || 0,
											},
										});
									}}
									placeholder="0"
								/>
								M */}
					</div>

					<div className="h-[1px] w-[14px] bg-[#C7C9D9]"></div>

					<div className="flex items-center gap-2">
						{selectedExpInput?.value === 'months' ? (
							<TextInput
								placeholder="Max Months"
								type="number"
								max={100}
								pattern="\d*"
								step={1}
								value={experienceInMonths.max}
								onChange={e => {
									const selectedValue = Number(e?.target?.value.replace(/\D/g, '') || 0);
									if (selectedValue < experienceInMonths?.min) {
										setExpError(true);
									} else {
										setExpError(false);
									}

									setExperienceInMonths({
										max: Number(selectedValue),
										min: experienceInMonths?.min,
									});

									const calcValue = Math.floor(+(selectedValue / 12 + experienceInYears?.max) * 100) / 100;

									setPositionDetail({
										...positionDetail,
										experience: {
											max: calcValue,
											min: positionDetail?.experience?.min || 0,
										},
									});
								}}
							/>
						) : (
							<TextInput
								placeholder="Max Years"
								type="number"
								max={100}
								pattern="\d*"
								step={1}
								value={positionDetail.experience.max}
								onChange={e => {
									const selectedValue = +e.target.value.replace(/\D/g, '');

									if (selectedValue < positionDetail.experience.min) {
										setExpError(true);
									} else {
										setExpError(false);
									}

									setPositionDetail({
										...positionDetail,
										experience: {
											...positionDetail.experience,
											max: selectedValue,
										},
									});
								}}
							/>
						)}

						{/* <Select
									menuPlacement={'auto'}
									classNamePrefix="my-select-plans"
									className="single-value-select-tag hideIndicator min-w-[50px]"
									options={generateRangeValuesArray(81, 0).filter(el => +el.value >= +(experienceInYears?.min ?? 0))}
									value={{
										label: String(experienceInYears?.max),
										value: String(experienceInYears?.max),
									}}
									onChange={(data: SingleValue<Dropdown>) => {
										const selectedValue = Number(data?.value || 0);
										setExperienceInYears({
											max: Number(selectedValue),
											min: experienceInYears?.min,
										});

										const calcValue = Math.floor(+(experienceInMonths?.max / 12 + selectedValue) * 100) / 100;

										setPositionDetail({
											...positionDetail,
											experience: {
												max: calcValue,
												min: positionDetail?.experience?.min || 0,
											},
										});
									}}
									placeholder="0"
								/>
								Y<div className="h-[1px] w-[14px] bg-[#C7C9D9]"></div>
								<Select
									menuPlacement={'auto'}
									styles={{
										multiValue: (baseStyles, state) => ({
											...baseStyles,
											backgroundColor: '#111E2C',
										}),
									}}
									classNamePrefix="my-select-plans"
									className="single-value-select-tag hideIndicator min-w-[50px]"
									options={generateRangeValuesArray(12, 0)}
									value={{
										label: String(experienceInMonths?.max),
										value: String(experienceInMonths?.max),
									}}
									onChange={(data: SingleValue<Dropdown>) => {
										const selectedValue = Number(data?.value || 0);
										setExperienceInMonths({
											max: Number(selectedValue),
											min: experienceInMonths?.min,
										});

										const calcValue = Math.floor(+(selectedValue / 12 + experienceInYears?.max) * 100) / 100;

										setPositionDetail({
											...positionDetail,
											experience: {
												max: calcValue,
												min: positionDetail?.experience?.min || 0,
											},
										});
									}}
									placeholder="0"
								/>
								M */}
					</div>
				</div>

				{expError ? (
					<div className="mt-2 text-sm text-red-600 dark:text-red-500">Max Exp can't be less then Min Exp</div>
				) : null}
			</div>

			{/* InternShip Duration */}
			{positionDetail?.workingMode &&
				(positionDetail?.workingMode === WorkingMode.INTERNSHIP ||
					positionDetail?.workingMode === WorkingMode.INTERNSHIP_TO_FULL_TIME) ? (
				<div>
					<div className="mb-2 block">
						<Label htmlFor="title" value="Internship Duration (in months)" className={`${labelStyle}`} />
						<span className="text-red-600 pl-1">*</span>
					</div>
					<div className="flex items-center gap-[8px]">
						<Button
							className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
							color="light"
							disabled={
								(positionDetail.internshipDuration !== undefined && positionDetail.internshipDuration <= 1) ||
								positionDetail.internshipDuration === undefined
							}
							onClick={() => {
								setPositionDetail({
									...positionDetail,
									internshipDuration:
										positionDetail.internshipDuration !== undefined ? --positionDetail.internshipDuration : 1,
								});
							}}
						>
							<IoMdRemove className="w-[24px] h-[24px]" />
						</Button>

						<TextInput
							min={1}
							max={12}
							onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
							type="number"
							placeholder="2"
							name="internshipDuration"
							value={positionDetail.internshipDuration || 0}
							onChange={event => {
								if (+event?.target?.value <= 12)
									setPositionDetail({ ...positionDetail, internshipDuration: +(event?.target?.value || 0) });
							}}
							className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow"
						/>

						<Button
							className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
							color="light"
							disabled={positionDetail.internshipDuration !== undefined && positionDetail.internshipDuration >= 12}
							onClick={() => {
								setPositionDetail({
									...positionDetail,
									internshipDuration:
										positionDetail.internshipDuration !== undefined ? ++positionDetail.internshipDuration : 1,
								});
							}}
						>
							<IoMdAdd className="w-[24px] h-[24px]" />
						</Button>
					</div>
				</div>
			) : null}

			{/* Internship Salary range */}
			{positionDetail?.workingMode &&
				(positionDetail?.workingMode === WorkingMode.INTERNSHIP ||
					positionDetail?.workingMode === WorkingMode.INTERNSHIP_TO_FULL_TIME) ? (
				<div>
					<div className="mb-2 block">
						<Label htmlFor="title" value="Internship Salary range (per month INR)" className={`${labelStyle}`} />
						<span className="text-red-600 pl-1">*</span>
					</div>

					<div className="flex items-center gap-2">
						<TextInput
							min={0}
							max={100}
							step="any"
							onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
							type="number"
							placeholder="0"
							name="internshipDuration"
							value={positionDetail?.internshipSalary?.min || 0}
							onChange={e => {
								const selectedValue = e.target.value || 0;
								if (+selectedValue <= 100) {
									setPositionDetail({
										...positionDetail,
										internshipSalary: {
											min: +selectedValue,
											max:
												+selectedValue > +(positionDetail?.internshipSalary?.max ?? 0)
													? +selectedValue
													: +(positionDetail?.internshipSalary?.max ?? 0),
										},
									});
								}
							}}
							className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow max-w-[50px]"
						/>
						<div className="h-[1px] w-[14px] bg-[#C7C9D9]"></div>
						<TextInput
							min={0}
							max={100}
							step="any"
							onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
							type="number"
							placeholder="2"
							name="internshipDuration"
							value={positionDetail?.internshipSalary?.max || 0}
							onChange={e => {
								const selectedValue = e.target.value || 0;
								if (+selectedValue <= 100) {
									setPositionDetail({
										...positionDetail,
										internshipSalary: { max: +selectedValue, min: +(positionDetail?.internshipSalary?.min ?? 0) },
									});
								}
							}}
							className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow max-w-[50px]"
						/>
						K
					</div>
				</div>
			) : null}

			{/* Salary range */}
			{positionDetail?.workingMode && positionDetail?.workingMode !== WorkingMode.INTERNSHIP && (
				<div>
					<div className="mb-2 block">
						<Label htmlFor="title" value="Salary range (lakhs per annum)" className={`${labelStyle}`} />
						<span className="text-red-600 pl-1">*</span>
					</div>

					<div className="flex items-center gap-2">
						<TextInput
							min={0}
							max={80}
							step="any"
							onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
							type="number"
							placeholder="0"
							name="internshipDuration"
							value={positionDetail?.salaryRange?.min || 0}
							onChange={e => {
								const selectedValue = e.target.value || 0;
								if (+selectedValue <= 80) {
									setPositionDetail({
										...positionDetail,
										salaryRange: {
											min: +selectedValue,
											max:
												+selectedValue > +positionDetail?.salaryRange?.max
													? +selectedValue
													: +positionDetail?.salaryRange?.max,
										},
									});
								}
							}}
							className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow max-w-[50px]"
						/>
						<div className="h-[1px] w-[14px] bg-[#C7C9D9]"></div>
						<TextInput
							min={0}
							max={80}
							step="any"
							onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
							type="number"
							placeholder="2"
							name="internshipDuration"
							value={positionDetail?.salaryRange?.max || 0}
							onChange={e => {
								const selectedValue = e.target.value || 0;
								if (+selectedValue <= 80) {
									setPositionDetail({
										...positionDetail,
										salaryRange: { max: +selectedValue, min: positionDetail?.salaryRange?.min },
									});
								}
							}}
							className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow max-w-[50px]"
						/>
						LPA
					</div>
				</div>
			)}

			{/* Location */}
			<div>
				<div className="mb-2 block">
					<Label htmlFor="title" value="Location & work type" className={`${labelStyle}`} />
					<span className="text-red-600 pl-1">*</span>
				</div>
				<div className="flex gap-4">
					<div className="flex-1">

						<Select
							menuPlacement={'auto'}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							closeMenuOnSelect={true}
							placeholder="Mumbai"
							isMulti={true}

							options={locationPreferenceOptions.map(location => ({ value: location, label: location }))}
							value={positionDetail?.workLocations?.map(location => ({ value: location, label: location }))}
							onChange={(val: MultiValue<Dropdown>) => {
								console.log('val:', val);

								setPositionDetail({
									...positionDetail,
									workLocations: val.map(item => item.value),
								});
							}}

						/>
					</div>
					<div className="flex-1">
						<Select
							menuPlacement={'auto'}
							options={positionTypes}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							value={positionTypes.map(title =>
								title.label === positionDetail.positionType
									? { label: positionDetail.positionType, value: positionDetail.positionType }
									: null,
							)}
							onChange={(val: SingleValue<Dropdown>) => {
								if (val?.value) {
									setPositionDetail({ ...positionDetail, positionType: val.value as PositionType });
								}
							}}
						/>
					</div>
				</div>
			</div>

			{/* Job Description */}
			<div>
				<div className="mb-2 block">
					<Label htmlFor="description" value="Job Description" className={`${labelStyle}`} />
					<span className="text-red-600 pl-1">*</span>
				</div>

				<ReactQuill
					className="rounded-[12px] border [&_.ql-toolbar]:!border-[0px] [&_.ql-toolbar]:!border-b-[1px] [&_.ql-container]:max-h-[300px] [&_.ql-container]:overflow-auto [&_.ql-container]:!border-[0px] dark:text-white bg-gray-50 dark:bg-[#374151]"
					value={positionDetail.description}
					onChange={data => {
						if (data && positionDetail.title) {
							setPositionDetail({
								...positionDetail,
								description: data,
							});
						}
					}}
				/>
			</div>

			{/* Number of openings */}
			<div>
				<div className="mb-2 block">
					<Label htmlFor="vacancy" value="Number of openings" className={`${labelStyle}`} />
					<span className="text-red-600 pl-1">*</span>
				</div>

				<div className="flex items-center gap-[8px]">
					<Button
						className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
						color="light"
						disabled={positionDetail.vacancy !== undefined && positionDetail.vacancy <= 1}
						onClick={() => {
							positionDetail.vacancy !== undefined &&
								setPositionDetail({ ...positionDetail, vacancy: --positionDetail.vacancy });
						}}
					>
						<IoMdRemove className="w-[24px] h-[24px]" />
					</Button>

					<TextInput
						min={1}
						max={50}
						onWheel={e => document.activeElement instanceof HTMLElement && document.activeElement.blur()}
						type="number"
						placeholder="2"
						name="vacancy"
						value={positionDetail.vacancy}
						onChange={event => {
							if (+event?.target?.value <= 50) setPositionDetail({ ...positionDetail, vacancy: +event?.target?.value });
						}}
						className="[&_input:focus]:ring-transparent [&_input:focus]:border-[#1C64F2] [&_input]:text-center RemoveInputArrow"
					/>

					<Button
						className="text-[#1C1C28] rounded-[8px] p-[8px] [&>span]:!p-0"
						color="light"
						disabled={positionDetail.vacancy !== undefined && positionDetail.vacancy >= 50}
						onClick={() => {
							positionDetail.vacancy !== undefined &&
								setPositionDetail({ ...positionDetail, vacancy: ++positionDetail.vacancy });
						}}
					>
						<IoMdAdd className="w-[24px] h-[24px]" />
					</Button>
				</div>
			</div>

			{/* Prep Material Links */}
			<div>
				<div className="mb-2 block">
					<Label htmlFor="prepMaterial" value="Prep Material Links" className={`${labelStyle}`} />
				</div>
				<div className="space-y-2">
					{prepMaterialLinks.map((link, index) => (
						<div key={index} className="flex gap-2 items-center">
							<TextInput
								type="url"
								value={link}
								onChange={e => {
									const newLinks = [...prepMaterialLinks];
									newLinks[index] = e.target.value;
									setPrepMaterialLinks(newLinks);
								}}
								placeholder="https://..."
								className="flex-1"
							/>
							{prepMaterialLinks.length > 1 && (
								<Button
									color="light"
									size="sm"
									onClick={() => {
										const newLinks = prepMaterialLinks.filter((_, i) => i !== index);
										setPrepMaterialLinks(newLinks);
									}}
									className="text-red-600 hover:text-red-700"
								>
									<IoMdRemove className="w-[20px] h-[20px]" />
								</Button>
							)}
						</div>
					))}
					<Button
						color="light"
						size="sm"
						onClick={() => setPrepMaterialLinks([...prepMaterialLinks, ''])}
						className="mt-2"
					>
						<IoMdAdd className="w-[20px] h-[20px] mr-1" />
						Add Link
					</Button>
				</div>
			</div>

			{/* Show Prep Material to Student Toggle */}
			<div className="flex items-center gap-2">
				<Label htmlFor="showPrepMaterialToStudent" value="Show Prep Material to Students" className={`${labelStyle}`} />
				<ToggleSwitch
					id="showPrepMaterialToStudent"
					checked={showPrepMaterialToStudent}
					onChange={setShowPrepMaterialToStudent}
					theme={{
						toggle: {
							checked: {
								color: {
									blue: 'bg-[#7777E7] border-[#7777E7]',
								},
							},
						},
					}}
				/>
			</div>

			<div className="bg-white dark:bg-[#111E2C] sticky bottom-0 mb-[-16px] md:mb-[-24px] mx-[-16px] md:mx-[-24px] shadow-up px-[16px] md:px-[24px] rounded-b-md">
				<Button
					type="submit"
					color="purplePlaced"
					className="my-3 w-full"
					disabled={
						expError ||
						!positionDetail.title ||
						editPositionFn.isLoading ||
						mandatoryselectedSkills.length < 2 ||
						// !positionDetail.location ||
						!positionDetail.workLocations ||
						!positionDetail.positionType ||
						!positionDetail.vacancy ||
						!(positionDetail.vacancy > 0) ||
						!positionDetail.workingMode ||
						(!positionDetail.salaryRange.min &&
							!positionDetail.salaryRange.max &&
							[WorkingMode.FULL_TIME, WorkingMode.INTERNSHIP_TO_FULL_TIME].includes(positionDetail.workingMode)) ||
						!positionDetail.numberOfRounds ||
						!(positionDetail.numberOfRounds > 0) ||
						!positionDetail.techStack ||
						// !positionDetail.interviewFormat ||
						// !(
						// 	!positionDetail?.isFaceToFacePosition ||
						// 	(positionDetail?.isFaceToFacePosition && positionDetail.interviewFormat)
						// ) ||
						(!!positionDetail.minGradYear && !_.inRange(positionDetail.minGradYear, 1950, currentYear + 1)) ||
						((positionDetail?.workingMode === WorkingMode.INTERNSHIP ||
							positionDetail?.workingMode === WorkingMode.INTERNSHIP_TO_FULL_TIME) &&
							!positionDetail?.internshipSalary) ||
						((positionDetail?.workingMode === WorkingMode.INTERNSHIP ||
							positionDetail?.workingMode === WorkingMode.INTERNSHIP_TO_FULL_TIME) &&
							!(positionDetail?.internshipDuration && positionDetail?.internshipDuration > 0))
					}
				>
					{editPositionFn.isLoading ? <Loader /> : 'Continue'}
				</Button>
			</div>
		</form>
	);
};

export default EditPosition;
